import React, { useCallback, useRef } from "react";
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';

interface IScoutingMap {
    pins: Array<any>;
    onPinClick: (data: any) => void;
    center?: any;
    zoom?: any
}

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const defaultCenter = { lat: 51.4980, lng: -0.0090 };
const mapContainerStyle = { width: '100%', height: '100%' };
const MAP_STYLES = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e5e7eb' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d1d5db' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d9dbe0' }] },
];

const centerOffset = (width: any, height: any) => ({ x: -(width / 2), y: -(height / 2) });

const MAP_OPTIONS = {
    disableDefaultUI: true,
    zoomControl: false,
    styles: MAP_STYLES,
    gestureHandling: 'greedy',
};

const ScoutingMap = ({
    pins,
    onPinClick,
    center = defaultCenter,
    zoom = 13,
}: IScoutingMap) => {
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'sixd-google-map',
        googleMapsApiKey,
    });

    const mapRef = useRef(null);
    const onLoad = useCallback((map: any) => { mapRef.current = map; }, []);
    const onUnmount = useCallback(() => { mapRef.current = null; }, []);

    const handleZoom = (delta: any) => {
        const map: any = mapRef.current;
        if (!map) return;
        map.setZoom((map.getZoom() ?? zoom) + delta);
    };

    if (loadError) {
        return (
            <div className="sv-map sv-map--fallback">
                <p>Couldn't load Google Maps. Check your API key / network.</p>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="sv-map sv-map--fallback">
                <p>Loading map…</p>
            </div>
        );
    }

    return (
        <div className="sv-map">
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={zoom}
                options={MAP_OPTIONS}
                onLoad={onLoad}
                onUnmount={onUnmount}
            >
                {pins?.map((item: any) => {
                    const pin = item?.location;
                    const lat = pin?.coordinates?.coordinates?.[0];
                    const lng = pin?.coordinates?.coordinates?.[1];
                    return (
                        <OverlayView
                            key={pin.id}
                            position={{ lat: lat, lng: lng }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            getPixelPositionOffset={centerOffset}
                        >
                            <button
                                className={`sv-pin${pin.active ? ' sv-pin--active' : ''}`}
                                onClick={() => onPinClick?.(item)}
                                aria-label={pin?.name || 'Location pin'}
                                title={pin?.name}
                            >
                                <img src={pin?.thumbnail || '/assets/images/yellow-6th.png'} alt="image" className="sv-pin__img" />
                                {pin?.active && <span className="sv-pin__dot" />}
                            </button>
                        </OverlayView>
                    )
                })}
            </GoogleMap>

            <div className="sv-map__zoom">
                <button className="sv-zoom-btn" aria-label="Zoom in" onClick={() => handleZoom(1)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
                <button className="sv-zoom-btn" aria-label="Zoom out" onClick={() => handleZoom(-1)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default ScoutingMap